import React, { useLayoutEffect, useState, useRef } from 'react'
import PropTypes from 'prop-types'
import BigNumber from 'bignumber.js'

// core
import { useGlobal } from '_core/hooks/useGlobal'
import { CurrencyConverterView } from './CurrencyConverterView'

const DEFAULT_SOURCE_VALUE = BigNumber(1)

const escapeText = text =>
  text.replace(/[.+*?^$|()[\]\\]/g, '\\$&').replace(/\\/g, '\\\\')

export const RawCurrencyConverter = props => {
  const {
    time,
    rates,
    source,
    currencies,
    setSource,
    render,
    Component,
  } = props

  const {
    config: { PRICE_FORMAT },
  } = useGlobal()

  const sourceInput = useRef()
  const cursorShift = useRef(0)
  const [target, setTarget] = useState()
  const [sourceValue, setSourceValue] = useState(DEFAULT_SOURCE_VALUE)

  useLayoutEffect(() => {
    if (sourceInput.current != null) {
      sourceInput.current.setCursor(cursorShift.current)
      cursorShift.current = 0
    }
  }, [sourceInput.current])

  if (source == null) {
    setSource(`${rates.find(item => item.value === 1).id}`)
  } else if (target == null) {
    setTarget(`${rates.find(item => item.value !== 1).id}`)
  }

  const {
    groupSeparator,
    decimalSeparator,
    fractionGroupSize,
    fractionGroupSeparator,
  } = PRICE_FORMAT

  const spaceChars = [groupSeparator, fractionGroupSeparator]
  const separators = [decimalSeparator, ...spaceChars]
  const validChars = ['0-9', ...separators]

  const spaceReg = new RegExp(`[${spaceChars.map(escapeText).join('')}]`, 'u')
  const bannedReg = new RegExp(`[^${validChars.map(escapeText).join('')}]`, 'u')
  const separatorsReg = new RegExp(
    `[${separators.map(escapeText).join('')}]`,
    'u'
  )

  const sourceRate = rates.find(item => `${item.id}` === `${source}`)
  const targetRate = rates.find(item => `${item.id}` === `${target}`)
  const sourceCurrency = currencies.find(item => `${item.id}` === `${source}`)
  const targetCurrency = currencies.find(item => `${item.id}` === `${target}`)

  const sourceRateNumber =
    sourceRate != null ? BigNumber(sourceRate.value) : null
  const targetRateNumber =
    targetRate != null ? BigNumber(targetRate.value) : null

  const targetValue =
    sourceRateNumber != null && targetRateNumber != null && sourceValue != null
      ? targetRateNumber.dividedBy(sourceRateNumber).multipliedBy(sourceValue)
      : null

  const options = rates.map(rate =>
    currencies.find(currency => `${rate.id}` === `${currency.id}`)
  )

  const flipOptions = () => {
    setTarget(source)
    setSource(target)
  }

  const onTargetChange = event =>
    `${event.target.value}` === `${source}`
      ? flipOptions()
      : setTarget(event.target.value)

  const onSourceChange = event =>
    `${event.target.value}` === `${target}`
      ? flipOptions()
      : setSource(event.target.value)

  const unformatValue = value =>
    value != null ? value.toFormat({ decimalSeparator }) : undefined

  const getValueScales = (value, currency) => {
    const valueFract = unformatValue(value).split(decimalSeparator)[1]
    const maxScale = currency.scale

    return {
      valueScale: valueFract != null ? valueFract.replace(/0*$/, '').length : 0,
      minScale: maxScale > fractionGroupSize ? fractionGroupSize : maxScale,
      maxScale,
    }
  }

  const getValueScale = ({ valueScale, minScale, maxScale }) =>
    valueScale > maxScale
      ? maxScale
      : valueScale < minScale
      ? minScale
      : valueScale

  const formatSourceValue = (value = sourceValue) => {
    if (source == null || value == null) {
      return null
    }

    const scale = getValueScale(getValueScales(value, sourceCurrency))
    return value.toFormat(scale, 1, PRICE_FORMAT)
  }

  const formatTargetValue = (value = targetValue) => {
    if (target == null || value == null) {
      return null
    }

    const scale = getValueScale(getValueScales(value, targetCurrency))
    return value.toFormat(scale, 1, PRICE_FORMAT)
  }

  const onSourceValueChange = event => {
    let oldValue
    let oldValueInput
    let oldValueFormat
    let value = event.target.value

    const scale = sourceCurrency.scale
    const unzero = formatSourceValue(BigNumber(0)).replace(/^0/, '')

    if (!value || value === unzero) {
      setSourceValue(null)
      return
    }

    if (value.search(bannedReg) >= 0) {
      oldValue = sourceValue != null ? sourceValue : DEFAULT_SOURCE_VALUE
      oldValueInput = oldValue.toFormat(scale, 1, PRICE_FORMAT)
      oldValueFormat = formatSourceValue(oldValue)

      cursorShift.current = -1
      value = oldValueInput
    } else {
      oldValue = sourceValue != null ? sourceValue : BigNumber(value)
      oldValueInput = oldValue.toFormat(scale, 1, PRICE_FORMAT)
      oldValueFormat = formatSourceValue(oldValue)

      if (
        value.split(separatorsReg).length !==
        oldValueFormat.split(separatorsReg).length
      ) {
        cursorShift.current = 0
        value = oldValueInput
      }
    }

    const parsed = value
      .split(decimalSeparator)
      .map((part, index) => {
        const result = part.split(spaceReg).join('')
        return index > 0 ? result.substring(0, scale) : result
      })
      .join(decimalSeparator)

    const newValue = BigNumber(parsed)
    const newValueInput = newValue.toFormat(scale, 1, PRICE_FORMAT)
    const newValueFormat = formatSourceValue(newValue)

    if (newValueInput !== oldValue) {
      cursorShift.current =
        newValueFormat.split(spaceReg).length -
        oldValueFormat.split(spaceReg).length
    }

    setSourceValue(parsed.length > 0 ? newValue : null)
  }

  const childProps = {
    time,
    source,
    target,
    options,
    sourceValue: unformatValue(sourceValue),
    targetValue: unformatValue(targetValue),
    sourceValueFormat: formatSourceValue(),
    targetValueFormat: formatTargetValue(),
    flipOptions,
    onSourceChange,
    onTargetChange,
    onSourceValueChange,
    ref: sourceInput,
  }

  return render != null ? render(childProps) : <Component {...childProps} />
}

RawCurrencyConverter.defaultProps = {
  Component: CurrencyConverterView,
}

RawCurrencyConverter.propTypes = {
  // self props
  render: PropTypes.func,
  Component: PropTypes.elementType,
  source: PropTypes.string,
  rates: PropTypes.arrayOf(PropTypes.object).isRequired,
  currencies: PropTypes.arrayOf(PropTypes.object).isRequired,
  setSource: PropTypes.func.isRequired,
}

export const CurrencyConverter = RawCurrencyConverter
